import {
  CheckOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileSearchOutlined,
  FileSyncOutlined,
  FileTextOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Flex,
  Form,
  Input,
  Modal,
  Spin,
  Steps,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  UploadFile,
  UploadProps,
  message,
  notification,
} from "antd";
import { useState } from "react";
import { ContractApi } from "src/api";
import { FormItem } from "src/components";
import { capitalizeFirstLetter, dictionary, i18n } from "src/utils";

const { Title } = Typography;
const { Dragger } = Upload;

const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE = Number(MAX_FILE_SIZE_MB) * 1024 * 1024;

type VerificationElementData = {
  id: string;
  name: string;
  email: string;
  integrationId: string;
  fileKey: string;
  buffer: Buffer;
  status: "processing" | "success" | "fail";
};

type UploadDocumentModal = {
  open: boolean;
  onCancel: () => void;
  loadDocument: (text: string, description: string) => void;
};

export default function DocumentUploadModal({ open, onCancel, loadDocument }: UploadDocumentModal) {
  const [messageApi, messageContextHolder] = message.useMessage();
  const [notificationApi, notificationContextHolder] = notification.useNotification();
  const [modalApi, modalContextHolder] = Modal.useModal();
  const [step, setStep] = useState(0);
  const [description, setDescription] = useState("");

  const [files, setFiles] = useState<UploadFile<File>[]>([]);

  const [tableData, setTableData] = useState<VerificationElementData[]>([]);

  // Único
  const [loading, setLoading] = useState(false);

  // Em lote
  const [verifying, setVerifying] = useState(false);
  const [processing, setProcessing] = useState(false);

  const configModalError = {
    title: i18n(dictionary.label.oops),
    content: (
      <>
        {i18n(dictionary.label.unread_file)}
        <br />
        {i18n(dictionary.label.check_file)}
        <br />
        {i18n(dictionary.label.try_another_file)}
      </>
    ),
  };

  async function handleOk() {
    //analisa o arquivo PDF
    const text = await verifyFile();

    //carrega o conteúdo no componente RTE
    loadDocument(text, description);

    // Finalizar;
    close();
  }

  // Verificação do arquivo;
  async function verifyFile(): Promise<string> {
    setStep((prevStep) => prevStep + 1);
    setVerifying(true);

    if (files[0].originFileObj) {
      const file = files[0].originFileObj as File;

      try {
        const response = await ContractApi.verifyFile(file);

        notificationApi.destroy();

        if (response.error) {
          setStep((prevStep) => prevStep - 1);

          modalApi.error(configModalError);

          notificationApi.error({
            message: i18n("ops"),
            description: i18n(`pages.documents.modal.type.multiple.notifications.verifyError`),
          });
          return "";
        }

        return response.text;
      } catch (err) {
        console.error(err);
        modalApi.error(configModalError);
      } finally {
        setVerifying(false);
      }
    }
    return "";
  }

  // Confirmação da importação;
  async function confirmImport() {
    setProcessing(true);

    setStep((prevStep) => prevStep + 1);
  }

  async function downloadDocument(fileName: string, buffer: Buffer) {
    const url = window.URL.createObjectURL(new Blob([buffer]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  }

  function deleteFromTable(id: string) {
    const newTable = [...tableData];
    const targetElementIndex = newTable.findIndex((element) => element.id === id);
    newTable.splice(targetElementIndex, 1);
    setTableData(newTable);
  }

  const props: UploadProps = {
    name: "file",
    multiple: false,
    maxCount: 1,
    fileList: files,
    accept: ".pdf",
    beforeUpload: () => false,
    onChange({ file, fileList }) {
      messageApi.destroy();
      if (file.type !== "application/pdf") {
        messageApi.error(i18n("fileUploadNotifications.invalidType"));
      } else if (file.size && file.size > MAX_FILE_SIZE) {
        messageApi.error(i18n("fileUploadNotifications.fileTooBig"));
      } else {
        setFiles(fileList);
      }
    },
  };

  function close() {
    // Limpa os estados
    setStep(0);
    setFiles([]);
    setTableData([]);
    // Fecha o modal
    onCancel();
  }

  return (
    <>
      {messageContextHolder}
      {notificationContextHolder}
      {modalContextHolder}
      <Modal
        maskClosable={false}
        closable={loading || verifying || processing ? false : true}
        width={420}
        open={open}
        onCancel={close}
        title={
          <Title
            style={{ margin: 0 }}
            level={4}
          >
            {`${i18n(dictionary.label.upload_document)}`}
          </Title>
        }
        footer={
          !verifying && (
            <Flex
              align="center"
              justify={"flex-end"}
              gap={10}
            >
              <Button onClick={close}>{i18n(dictionary.action.close)}</Button>
              <Button
                type="primary"
                onClick={handleOk}
                disabled={files.length !== 1 || (step === 1 && (!tableData.length || verifying))}
                loading={(open && loading) || (!open && processing)}
              >
                {i18n(dictionary.action.save)}
              </Button>
            </Flex>
          )
        }
      >
        {!open && (
          <Tag
            style={{
              margin: "8px 0 0 0",
              wordBreak: "break-word",
              whiteSpace: "normal",
            }}
            color="gold"
          >
            {i18n("pages.documents.modal.warning")}
          </Tag>
        )}
        <Flex
          style={{ margin: "15px 0 15px 0" }}
          vertical
          gap={20}
        >
          {!open && (
            <Steps
              current={step}
              items={[
                {
                  title: i18n("pages.documents.modal.type.multiple.steps.1.title"),
                  description: i18n("pages.documents.modal.type.multiple.steps.1.description"),
                  icon: <FileTextOutlined />,
                },
                {
                  title: i18n("pages.documents.modal.type.multiple.steps.2.title"),
                  description: i18n("pages.documents.modal.type.multiple.steps.2.description"),
                  icon: <FileSearchOutlined />,
                },
                {
                  title: i18n("pages.documents.modal.type.multiple.steps.3.title"),
                  description: i18n("pages.documents.modal.type.multiple.steps.3.description"),
                  icon: <FileSyncOutlined />,
                },
              ]}
            />
          )}

          {!verifying && (
            <Form layout="vertical">
              <FormItem
                label={capitalizeFirstLetter(i18n(dictionary.label.document_name)) + ":"}
                name="document_name"
              >
                <Input
                  placeholder={i18n(dictionary.label.write_here)}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </FormItem>
            </Form>
          )}
          {step === 0 && (
            <FormItem
              label={capitalizeFirstLetter(i18n(dictionary.label.document_name))}
              name="document_upload"
            >
              <Dragger {...props}>
                <p className="ant-upload-drag-icon">
                  <CloudUploadOutlined />
                </p>
                <p className="ant-upload-text">{i18n(dictionary.label.click_upload_document)}</p>
                <p className="ant-upload-hint">
                  {i18n(dictionary.label.upload_document_limit, { limit: MAX_FILE_SIZE_MB })}
                </p>
              </Dragger>
            </FormItem>
          )}
          {(step === 1 || step === 2) &&
            (verifying ? (
              <Card>
                <Flex
                  vertical
                  align="center"
                  justify="center"
                  gap={25}
                >
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 100 }} />} />

                  {i18n(dictionary.label.upload_document_loading)}
                </Flex>
              </Card>
            ) : (
              <Table
                rowKey={(record) => record.id}
                size="small"
                scroll={{ x: true }}
                loading={false}
                pagination={{
                  pageSize: 5,
                  showSizeChanger: false,
                  simple: true,
                  style: { justifyContent: "center", marginBottom: 0 },
                }}
                dataSource={tableData}
                columns={[
                  {
                    title: i18n("pages.documents.modal.table.columns.employee"),
                    dataIndex: "name",
                  },
                  {
                    title: i18n(`pages.documents.modal.table.columns.${step === 1 ? "actions" : "status"}.label`),
                    dataIndex: "id",
                    width: 1,
                    align: "center",
                    render: (id, { fileKey, buffer, status }) => {
                      return step === 2 ? (
                        status === "processing" ? (
                          <LoadingOutlined style={{ color: "#884EA6" }} />
                        ) : status === "success" ? (
                          <CheckOutlined style={{ color: "#49AA19" }} />
                        ) : (
                          <CloseOutlined style={{ color: "#FF4D4F" }} />
                        )
                      ) : (
                        <Flex
                          justify="center"
                          align="center"
                          gap={10}
                        >
                          <Tooltip title={i18n("pages.documents.modal.table.columns.actions.buttons.download")}>
                            <Button
                              icon={<DownloadOutlined />}
                              onClick={() => downloadDocument(fileKey, buffer)}
                            />
                          </Tooltip>
                          <Tooltip title={i18n("pages.documents.modal.table.columns.actions.buttons.delete")}>
                            <Button
                              icon={<DeleteOutlined />}
                              onClick={() => deleteFromTable(id)}
                            />
                          </Tooltip>
                        </Flex>
                      );
                    },
                  },
                ]}
              />
            ))}
        </Flex>
      </Modal>
    </>
  );
}
